// 組織設定 › IT 監控（`/org/kmgr`，SPEC #kinio-kms-ui「設定只有兩個家」）：組織層的啟用精靈與輪值表。
// SPEC 的分頁是「安全政策｜輪值表｜監控範本」；監控範本（M5-02）在 templates/TemplatesPage.tsx。
import { useEffect } from 'react';
import { Link, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { orgApi, type MyOrg } from '../../lib/planeApi';
import { OrgGate, relevantOrgs } from '../shell/OrgGate';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrAdminOrgs, useKmgrEnv, useOrgKmgrStatus } from './hooks';
import { Crumbs, errText, NS } from './parts';
import { EnableWizard } from './EnableWizard';
import { OncallEditor, OncallList } from './Oncall';
import { AdminKeys } from './AdminKeys';
import { TemplatesRoutes } from './templates/TemplatesPage';
import { HandoverPage } from './handover/HandoverPage';
import { BuiltOrgs } from './handover/BuiltOrgs';

export function OrgKmgrPage() {
  const { t } = useTranslation(NS);
  useKmgrEnv();
  const { orgs, homes, setScope, org: scopeOrg } = useScope();
  const [sp] = useSearchParams();
  const loc = useLocation();
  const candidates = relevantOrgs(kmgrAdminOrgs(orgs), homes);
  // 從總覽啟用卡進來帶 ?org=：範圍切到那個組織（組織管理頁一次只對一個組織）
  const want = sp.get('org');
  // 剛建好的組織（代建，M5-03）要等組織清單重讀後才在 candidates 裡：清單變了再試一次
  const wantReady = !!want && candidates.some((o) => o.org_id === want);
  useEffect(() => {
    if (want && want !== scopeOrg?.org_id && wantReady) setScope('org:' + want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want, wantReady]);
  const sub = loc.pathname.replace(/^\/org\/kmgr\/?/, '');
  const section = sub.startsWith('enable')
    ? 'enable'
    : sub.startsWith('oncall')
      ? 'oncall'
      : sub.startsWith('keys')
        ? 'keys'
        : sub.startsWith('templates')
          ? 'templates'
          : sub.startsWith('handover')
            ? 'handover'
            : sub.startsWith('built')
              ? 'built'
              : 'status';
  const title =
    section === 'enable' ? t('org.enable_title') : section === 'oncall' && sub !== 'oncall' ? t('oncall.edit_title') : section === 'handover' ? t('handover.title') : t('org.title');
  return (
    <section className="page km" data-testid="km-org-page">
      <Crumbs
        items={[
          { label: t('org.crumb_org'), to: '/org' },
          { label: t('org.title'), to: '/org/kmgr' },
          ...(section === 'keys' ? [{ label: t('org.tab_keys') }] : []),
          ...(section === 'handover' ? [{ label: t('handover.title') }] : section === 'built' ? [{ label: t('org.tab_built') }] : []),
          ...(section === 'templates' ? [{ label: t('org.tab_templates'), to: '/org/kmgr/templates' }, ...(sub.startsWith('templates/new') ? [{ label: t('tpl.new_title') }] : [])] : []),
          ...(section === 'enable' ? [{ label: t('org.enable_crumb') }] : section === 'oncall' ? [{ label: t('org.tab_oncall'), to: '/org/kmgr/oncall' }, ...(sub !== 'oncall' ? [{ label: t('oncall.edit_title') }] : [])] : []),
        ]}
      />
      <h1 className="page-title">{title}</h1>
      <OrgGate candidates={candidates} empty={<p className="card muted">{t('org.no_admin')}</p>}>
        {(org) => (
          <>
            {section !== 'enable' && (
              <nav className="km-tabs" aria-label={t('org.tabs')}>
                <Link to="/org/kmgr" aria-current={section === 'status' ? 'page' : undefined}>
                  {t('org.tab_status')}
                </Link>
                <Link to="/org/kmgr/oncall" aria-current={section === 'oncall' ? 'page' : undefined}>
                  {t('org.tab_oncall')}
                </Link>
                <Link to="/org/kmgr/keys" aria-current={section === 'keys' ? 'page' : undefined}>
                  {t('org.tab_keys')}
                </Link>
                <Link to="/org/kmgr/templates" aria-current={section === 'templates' ? 'page' : undefined} data-testid="km-org-tab-templates">
                  {t('org.tab_templates')}
                </Link>
                {org.built_by_partner && (
                  <Link to="/org/kmgr/handover" aria-current={section === 'handover' ? 'page' : undefined} data-testid="km-org-tab-handover">
                    {t('org.tab_handover')}
                  </Link>
                )}
                {org.kind === 'team' && !org.built_by_partner && (
                  <Link to="/org/kmgr/built" aria-current={section === 'built' ? 'page' : undefined} data-testid="km-org-tab-built">
                    {t('org.tab_built')}
                  </Link>
                )}
              </nav>
            )}
            <Routes>
              <Route index element={<OrgStatus org={org} />} />
              <Route path="enable" element={<EnableWizard key={org.org_id} org={org} />} />
              <Route path="oncall" element={<OncallList org={org} />} />
              <Route path="oncall/:scheduleId" element={<OncallEditor org={org} />} />
              <Route path="keys" element={<AdminKeys key={org.org_id} org={org} />} />
              <Route path="templates/*" element={<TemplatesRoutes key={org.org_id} org={org} />} />
              <Route path="handover" element={<HandoverPage key={org.org_id} org={org} />} />
              <Route path="built" element={<BuiltOrgs key={org.org_id} org={org} />} />
            </Routes>
          </>
        )}
      </OrgGate>
    </section>
  );
}

function OrgStatus({ org }: { org: MyOrg }) {
  const { t } = useTranslation(NS);
  const st = useOrgKmgrStatus(org);
  const homes = useQuery({ queryKey: ['org-homes', org.org_id], queryFn: () => orgApi.homes(org), staleTime: 60_000 });
  if (st.isLoading) return <SkeletonLines n={3} label={t('common.loading')} />;
  if (st.isError || !st.data)
    return (
      <div className="km-bar unk" role="status">
        <span className="ic" aria-hidden>
          ⊘
        </span>
        <span className="txt">{t('wizard.status_unavailable', { detail: errText(t, st.error) })}</span>
        <button className="btn btn-sm" onClick={() => void st.refetch()}>
          {t('common.retry')}
        </button>
      </div>
    );
  const d = st.data;
  const enabled = d.homes.filter((h) => h.kmgr_enabled);
  const name = (id: string) => homes.data?.find((h) => h.home_id === id)?.display_name ?? id;
  return (
    <div data-testid="km-org-status">
      <section className="card" style={{ marginBottom: 'var(--space-3)' }}>
        <dl className="km-dl">
          <dt>{t('org.dpa')}</dt>
          <dd>{d.dpa.accepted_version ? t('org.dpa_accepted', { v: d.dpa.accepted_version, time: (d.dpa.accepted_at ?? '').slice(0, 10) }) : t('org.dpa_none')}</dd>
          <dt>{t('org.homes')}</dt>
          <dd>
            {enabled.length ? (
              <ul className="plain-list">
                {enabled.map((h) => (
                  <li key={h.home_id}>
                    {name(h.home_id)} · <Link to={`/sites/kmgr/${encodeURIComponent(h.home_id)}`}>{t('org.home_settings')}</Link>
                  </li>
                ))}
              </ul>
            ) : (
              t('org.homes_none')
            )}
          </dd>
        </dl>
      </section>
      <Link className={enabled.length ? 'btn' : 'btn btn-primary'} to="/org/kmgr/enable">
        {enabled.length ? t('org.open_wizard') : t('org.start_wizard')}
      </Link>
    </div>
  );
}
